import type { SourceInfo, StreamSummary } from './types';
import { useCounter } from './useCounter';
import { QualityWave } from './QualityWave';
import { ObsSettings } from './ObsSettings';
import { SrtLinkPanel } from './SrtLinkPanel';
import { StreamPreview } from './StreamPreview';
import { backlogSec, delaySec, serverName } from './format';
import { viewersText } from './PlatformBlock';
import { EditButton, SecondaryButton, StatusBadge } from './ui';
import { Icon } from '../ui/Icon';

function hms(sec: number) {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(Math.floor(sec / 3600))}:${p(Math.floor((sec % 3600) / 60))}:${p(sec % 60)}`;
}

/**
 * Health of the input. "Good" is not a state of the system (09 vocabulary, AUDIT ST-05): then only
 * the words ("Steady") show; a problem gets its state chip.
 */
const HEALTH = {
  good: null,
  warning: { c: 'warn', t: 'WARNING' },
  bad: { c: 'fail', t: 'BAD' },
  offline: { c: 'idle', t: 'OFFLINE' },
} as const;

/**
 * Top summary: on air or not (decided by the sender), duration, input metrics, health — one panel
 * of mono numbers as in the system's ScreenStatus, not a page title (AUDIT ST-01).
 */
export function LiveSummary({ s, now, needAttention, onAir, onObs }: { s: StreamSummary; now: number; needAttention: number; onAir: number; onObs: () => void }) {
  const dur = s.live && s.since ? hms(Math.max(0, Math.floor((now - Date.parse(s.since)) / 1000))) : '';
  const metrics = [
    { label: 'Bitrate', value: s.bitrateMbps?.toFixed(2) ?? '—', unit: 'Mbps' },
    { label: 'Frame rate', value: s.fps ? String(Math.round(s.fps)) : '—', unit: 'fps' },
    { label: 'Keyframes', value: s.keyframeSec?.toFixed(1) ?? '—', unit: 's' },
    { label: 'Resolution', value: s.resolution ?? '—', unit: '' },
  ];
  const health = HEALTH[s.health];
  const chip = health && <StatusBadge color={health.c} border="solid">{health.t}</StatusBadge>;
  return (
    <section aria-label="Stream summary" className="sx-panel flex flex-col gap-3 md:flex-row md:flex-wrap md:items-center md:gap-x-7 md:gap-y-3 md:px-4 md:py-3">
      <div className="flex items-center justify-between gap-3 md:mr-auto md:block">
        <div className="flex flex-col gap-0.5">
          <h1 data-testid="input-state" className="m-0 text-base leading-5 font-semibold">{s.live ? "You're live" : 'Ready when you are'}</h1>
          <span className="text-xs text-muted">
            {s.live ? 'On air for ' : 'Off air · '}
            <span className="num font-mono text-text-soft">{s.live ? dur : `${onAir} destination${onAir === 1 ? '' : 's'} on air`}</span>
          </span>
        </div>
        {chip && <div className="md:hidden">{chip}</div>}
      </div>
      {s.live ? (
        <div className="grid grid-cols-2 gap-2 md:contents">
          {metrics.map((m) => (
            <div key={m.label} className="flex flex-col gap-1 rounded-lg border border-line bg-bg px-3 py-2.5 md:border-0 md:bg-transparent md:p-0">
              <span className="text-xs text-muted">{m.label}</span>
              <span className="num font-mono text-xl leading-6 font-medium">{m.value} <span className="text-xs text-muted">{m.unit}</span></span>
            </div>
          ))}
          {s.viewers && <Watching v={s.viewers} />}
        </div>
      ) : (
        <div className="flex flex-col gap-2 md:max-w-[420px]">
          <span className="text-sm text-text-soft">Start streaming in OBS with your server and key. Destinations that are on go live as soon as the stream arrives.</span>
          <SecondaryButton className="self-start" onClick={onObs}>OBS settings</SecondaryButton>
        </div>
      )}
      <div className="hidden flex-col gap-1 md:flex">
        <span className="text-xs text-muted">Health</span>
        <span className="flex items-center gap-2.5">{chip}<span className="text-sm leading-6 text-text-soft">{s.healthText || '—'}</span></span>
      </div>
      {s.healthText && <div className="text-[13px] text-text-soft md:hidden">{s.healthText}</div>}
      {needAttention > 0 && <div className="flex items-center gap-1.5 text-[13px] text-warn md:hidden"><Icon name="state-warn" size={12} />{needAttention} of the destinations need attention</div>}
    </section>
  );
}

/** Watching: the sum of the platforms that report viewers, its change over 5 minutes, the split in a tooltip. */
function Watching({ v }: { v: NonNullable<StreamSummary['viewers']> }) {
  const split = v.parts.map((p) => `${p.name} ${viewersText(p.viewers)}`).join(', ');
  const d = v.delta ?? 0;
  return (
    <div data-testid="viewers-total" title={`Sum of platforms that report viewers${split ? `: ${split}` : ''}`}
      className="flex flex-col gap-1 rounded-lg border border-line bg-bg px-3 py-2.5 md:border-0 md:bg-transparent md:p-0">
      <span className="text-xs text-muted">Watching</span>
      <span className="num flex items-baseline gap-2 font-mono text-xl leading-6 font-medium">
        <span className="flex items-center gap-1.5 self-center"><Icon name="eye" size={16} className="text-muted" />{viewersText(v.total)}</span>
        {/* the change is a number, not a state: no state colour, no arrow glyph (02, 06) */}
        {d !== 0 && (
          <span className="text-xs text-text-soft">
            <span aria-hidden="true">{d > 0 ? '+' : '−'}{viewersText(Math.abs(d))}</span>
            <span className="sr-only">{d > 0 ? 'up' : 'down'} {Math.abs(d)} in 5 minutes</span>
          </span>
        )}
      </span>
    </div>
  );
}

/** Phone replacement for the signal map: the source with its own counter, quality and inline settings. */
export function SourceWidget({ source, now, editing, onEdit }: { source: SourceInfo; now: number; editing: boolean; onEdit: () => void }) {
  const sent = useCounter(source.received, now);
  return (
    <section aria-label="Signal source" className={`sx-panel flex flex-col gap-3 !p-3.5 ${editing ? 'border-accent' : source.sending ? 'border-live/[.45]' : 'border-idle/30'}`}>
      <div className="flex items-center gap-2.5">
        <span className="grid h-[30px] w-[30px] flex-none place-items-center rounded-sm border border-slot text-muted">
          <svg width="18" height="16" viewBox="0 0 26 22" aria-hidden="true"><rect x="1" y="1" width="24" height="15" rx="2" fill="none" stroke="currentColor" strokeWidth="1.6" /><path d="M8 21h10M13 16v5" stroke="currentColor" strokeWidth="1.6" /></svg>
        </span>
        <div className="min-w-0 flex-1"><div className="font-mono text-[10px] font-medium tracking-[.12em] text-muted">SOURCE · YOUR PC{source.sending && source.protocol ? <span data-testid="source-protocol"> · {source.protocol.toUpperCase()}</span> : null}</div><div className="truncate font-medium">{source.app}</div></div>
        <StatusBadge color={source.unknown ? 'idle' : source.sending ? 'live' : 'idle'} border="solid">{source.unknown ? '? NO DATA' : source.sending ? '● SENDING' : '○ NOT SENDING'}</StatusBadge>
        <EditButton editing={editing} label="OBS settings" size={44} onClick={onEdit} />
      </div>
      {source.preview && (
        <div className="flex gap-3">
          <StreamPreview stream={source.preview.stream} available={source.preview.available} now={now} width={150} height={84} />
          <div className="flex min-w-0 flex-col gap-1 font-mono text-[11px] text-text-soft">
            {source.encoder.split(' · ').map((part) => <span key={part} className="truncate">{part}</span>)}
          </div>
        </div>
      )}
      {source.delay && (
        <div data-testid="input-delay" className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5"
          title="Estimated from network round-trip (RTT) and how far OBS is behind its own clock">
          <span className="font-mono text-[11px] font-semibold text-muted">DELAY TO US</span>
          <span className="num font-mono text-[13px] font-medium">≈{delaySec(source.delay.toUsMs)}</span>
          <span className="num border-b border-dotted border-faint font-mono text-[11px] text-faint">
            RTT {source.delay.rttMs > 0 ? `${Math.round(source.delay.rttMs)} ms` : '—'} · backlog {backlogSec(source.delay.backlogMs)}
          </span>
        </div>
      )}
      <div className={`grid grid-cols-2 gap-x-3 gap-y-2 ${source.unknown ? 'opacity-50' : ''}`}>
        {[
          { l: 'Sent', v: `↑ ${sent}`, strong: true },
          { l: 'Upload', v: source.uploadMbps !== undefined ? `${source.uploadMbps.toFixed(1)} Mbps` : '—' },
          { l: 'Jitter', v: source.jitterMs !== undefined ? `${source.jitterMs} ms` : '—' },
          { l: 'Reconnects', v: String(source.reconnects) },
        ].map((m) => (
          <div key={m.l}><div className="text-[11px] text-muted">{m.l}</div><div className={`num font-mono text-[17px] font-medium ${m.strong ? 'text-text' : ''}`}>{m.v}</div></div>
        ))}
      </div>
      <QualityWave history={source.history} summary={source.summary} title="Upload · quality" height={52} />
      {source.srt && <SrtLinkPanel link={source.srt} compact />}
      {source.node && (
        <div data-testid="source-node" className={`flex items-center justify-between gap-2 font-mono text-[11px] ${source.unknown ? 'text-warn' : 'text-muted'}`}>
          <span>SERVER {serverName(source.node)}</span>
          {source.unknown && <span>unreachable, state unknown</span>}
        </div>
      )}
      {!source.preview && <div className="truncate border-t border-line pt-2 font-mono text-[11px] text-muted">{source.encoder}</div>}
      {editing && <div className="border-t border-line pt-3"><ObsSettings streamName={source.streamName} protocol={source.protocol} onDone={onEdit} /></div>}
    </section>
  );
}
